import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { Building07 } from "@untitledui/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { Avatar } from "@/components/base/avatar/avatar";
import { BadgeWithDot } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { EmptyValue } from "@/components/common";
import CopyableId from "@/components/Molecules/CopyableId/CopyableId";
import {
	OrganizationsTableFilters,
	type OrganizationsTableFiltersState,
} from "@/components/Molecules/FilterDrawer/OrganizationsTableFilters";
import type { ColumnDef } from "@/components/Molecules/Table";
import { CustomTableWrapper } from "@/components/Molecules/Table/CustomTableWrapper";
import { TableSearchEmptyState } from "@/components/Molecules/Table/TableSearchEmptyState";
import { CreateOrgModal } from "@/components/Organisms/CreateOrgModal/CreateOrgModal";
import { organizationListStatusesParam } from "@/features/organizations/organization-list-status";
import { PageHeader } from "@/components/Organisms/PageHeader/PageHeader";
import { PageLayout } from "@/components/Organisms/PageLayout/PageLayout";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useDebounced } from "@/hooks/use-debounced";
import { useTableSettings } from "@/hooks/use-table-settings";
import {
	type Organization,
	OrganizationStatusColorMap,
	organizationQueryOptions,
	useExportOrganizations,
} from "@/services/organization";
import {
	buildFilterParams,
	type FilterConfig,
} from "@/utils/build-filter-params";
import { downloadBlob, generateExportFilename } from "@/utils/download";
import { formatLongDate } from "@/utils/utils";

// Table settings constants
const TABLE_KEY = "admin.organizations";
const DEFAULT_COLUMN_ORDER = [
	"name",
	"id",
	"status.name",
	"oAuthProvider.name",
	"mfaEnabled",
	"totalUserCount",
	"createdat",
	"updatedat",
];
const DEFAULT_SORTING = [{ id: "name", desc: false }];
const DEFAULT_PAGE_SIZE = 10;

const normalizeColumnOrder = (columnOrder?: string[]) => {
	if (!Array.isArray(columnOrder) || columnOrder.length === 0) {
		return [...DEFAULT_COLUMN_ORDER];
	}

	const allowedColumns = new Set(DEFAULT_COLUMN_ORDER);
	const sanitized = columnOrder.filter((columnId) =>
		allowedColumns.has(columnId),
	);
	const unique = Array.from(new Set(sanitized));
	const missingDefaults = DEFAULT_COLUMN_ORDER.filter(
		(columnId) => !unique.includes(columnId),
	);

	return [...unique, ...missingDefaults];
};

// Serializable filter state for persistence (excludes date ranges)
interface PersistedFilters {
	organization?: string[];
	status?: string[];
	identityProvider?: string[];
}
const DEFAULT_FILTERS: PersistedFilters = {
	organization: [],
	status: [],
	identityProvider: [],
};

export const Route = createFileRoute(
	"/_authenticated/(admin-app)/_admin/admin/organizations",
)({
	component: OrganizationsIndex,
	validateSearch: (search) => {
		return z
			.object({
				filterOpen: z.boolean().optional(),
			})
			.parse(search);
	},
});

function OrganizationsIndex() {
	const navigate = useNavigate();
	const { t } = useAppTranslation();
	const queryClient = useQueryClient();
	const [pageSize, setPageSize] = useState(10);
	const [pageIndex, setPageIndex] = useState(0);
	const [searchTerm, setSearchTerm] = useState("");
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const [tableOptionsHost, setTableOptionsHost] =
		useState<HTMLDivElement | null>(null);
	const [filters, setFilters] = useState<OrganizationsTableFiltersState>({
		organization: [],
		status: [],
		createdDateRange: null,
		lastUpdateDateRange: null,
	});
	const [sortParams, setSortParams] = useState({
		"OrderBy.Fields": ["name"],
		"OrderBy.Descending": false,
	});

	// Track if persisted settings have been applied
	const hasAppliedPersistedSettings = useRef(false);

	// Table settings persistence
	const {
		settings: tableSettings,
		isLoaded: settingsLoaded,
		saveSorting,
		saveColumnOrder,
		saveColumnVisibility,
		saveSearchTerm,
		saveFilters,
		savePageSize,
	} = useTableSettings<PersistedFilters>({
		tableKey: TABLE_KEY,
		defaultSorting: DEFAULT_SORTING,
		defaultColumnOrder: DEFAULT_COLUMN_ORDER,
		defaultSearchTerm: "",
		defaultFilters: DEFAULT_FILTERS,
		defaultPageSize: DEFAULT_PAGE_SIZE,
	});

	// Apply persisted settings (search, filters, page size) when config has loaded
	useEffect(() => {
		if (!settingsLoaded || hasAppliedPersistedSettings.current) return;
		hasAppliedPersistedSettings.current = true;

		if (tableSettings.searchTerm) {
			setSearchTerm(tableSettings.searchTerm);
		}
		if (tableSettings.filters) {
			setFilters((prev) => ({
				...prev,
				organization: tableSettings.filters.organization ?? [],
				status: tableSettings.filters.status ?? [],
				identityProvider: tableSettings.filters.identityProvider ?? [],
			}));
		}
		if (
			Number.isFinite(tableSettings.pageSize) &&
			[10, 25, 50, 100].includes(tableSettings.pageSize)
		) {
			setPageSize(tableSettings.pageSize);
		}
	}, [
		settingsLoaded,
		tableSettings.searchTerm,
		tableSettings.filters,
		tableSettings.pageSize,
	]);

	const sortFieldMapping: Record<string, string[]> = {
		name: ["name"], //good
		id: ["id"], //good
		"status.name": ["statusCode"], //good
		"oAuthProvider.name": ["oAuthProvider.name"], //good
		mfaEnabled: ["mfaEnabled"],
		totalUserCount: ["totalUserCount"],
		createdat: ["CreatedAt"], //good
		updatedat: ["UpdatedAt"], //good
	};
	const startIndex = pageIndex * pageSize + 1;
	const exportMutation = useExportOrganizations();
	const debouncedFilters = useDebounced(filters, 500);

	const filterConfig: FilterConfig = {
		organization: { paramName: "organizationIds", transform: "join" },
		status: { paramName: "statuses", transform: "join" },
		"createdDateRange.start": {
			paramName: "CreatedAfter",
			transform: "dateStart",
		},
		"createdDateRange.end": {
			paramName: "CreatedBefore",
			transform: "dateEnd",
			dateStartField: "createdDateRange.start",
		},
		"lastUpdateDateRange.start": {
			paramName: "UpdatedAfter",
			transform: "dateStart",
		},
		"lastUpdateDateRange.end": {
			paramName: "UpdatedBefore",
			transform: "dateEnd",
			dateStartField: "lastUpdateDateRange.start",
		},
	};

	const filterParams = useMemo(() => {
		const baseParams = buildFilterParams(debouncedFilters, filterConfig);

		const idp = debouncedFilters.identityProvider?.[0];
		if (idp === "third-party") {
			baseParams.UsesThirdPartyAuthentication = true;
		} else if (idp === "first-party")
			baseParams.UsesThirdPartyAuthentication = false;

		return baseParams;
	}, [debouncedFilters]);

	const listQueryParams = useMemo(
		() => ({
			...filterParams,
			statuses: organizationListStatusesParam(debouncedFilters.status),
		}),
		[debouncedFilters.status, filterParams],
	);

	const orgCount = useQuery(
		organizationQueryOptions.count({
			SearchTerm: searchTerm,
			...listQueryParams,
			...sortParams,
		}),
	);

	const unFilteredOrgCount = useQuery(organizationQueryOptions.count({}));

	const allOrgs = useQuery(
		organizationQueryOptions.list(startIndex, pageSize, {
			SearchTerm: searchTerm,
			...listQueryParams,
			...sortParams,
		}),
	);

	const data = useMemo(() => {
		return allOrgs.data ?? [];
	}, [allOrgs.data]);

	const pageCount = useMemo(() => {
		if (!orgCount.data) return 0;
		return Math.ceil(orgCount.data / pageSize);
	}, [orgCount.data, pageSize]);

	const handleRefresh = async () => {
		setIsRefreshing(true);
		try {
			await allOrgs.refetch();
		} finally {
			setIsRefreshing(false);
		}
	};

	const handleExport = (visibleColumns?: string[]) => {
		const totalCount = orgCount.data ?? 0;
		if (totalCount === 0 || totalCount > 10000) return;

		exportMutation.mutate(
			{
				startIndex: 1,
				count: totalCount,
				params: {
					SearchTerm: searchTerm,
					...listQueryParams,
					...sortParams,
					columns: visibleColumns?.join(","),
				},
			},
			{
				onSuccess: (blob) => {
					downloadBlob(blob, generateExportFilename("organizations"));
				},
			},
		);
	};

	const handleRowClick = (organization: Organization) => {
		navigate({
			to: "/admin/organizations/$organizationId/details",
			params: { organizationId: organization.id },
			search: { mode: "view" },
		});
	};

	const handlePaginationChange = ({
		pageSize: newPageSize,
		pageIndex: newPageIndex,
	}: {
		pageSize: number;
		pageIndex: number;
	}) => {
		if (newPageSize !== pageSize) {
			setPageSize(newPageSize);
			setPageIndex(0);
			savePageSize(newPageSize);
		} else if (newPageIndex !== pageIndex) {
			setPageIndex(newPageIndex);
		}
	};

	const handleDebouncedSearchChange = (search: string) => {
		setSearchTerm(search);
		setPageIndex(0);
		// Save search term for persistence
		saveSearchTerm(search);
	};

	const handleFilterChange = (newFilters: OrganizationsTableFiltersState) => {
		setFilters(newFilters);
		// Save filters for persistence (only serializable fields)
		saveFilters({
			organization: newFilters.organization,
			status: newFilters.status,
			identityProvider: newFilters.identityProvider,
		});
	};

	const organizationColumns: ColumnDef<Organization>[] = [
		{
			id: "name",
			accessorKey: "name",
			header: t("admin.organizations.table.columns.organizationName"),
			enableSorting: true,
			meta: {
				headerProps: {
					isRowHeader: true,
				},
			},
			minSize: 300,
			cell: ({ row }) => {
				const org = row.original;
				return (
					<div className="flex items-center gap-3">
						<Avatar
							alt={org.name ?? undefined}
							placeholder={<Building07 className="size-6 text-gray-400" />}
							size="md"
						/>
						<div className="whitespace-nowrap">
							{org.name ? (
								<p className="text-sm font-medium text-primary">{org.name}</p>
							) : (
								<EmptyValue />
							)}
						</div>
					</div>
				);
			},
		},
		{
			id: "status.name",
			accessorKey: "status.name",
			header: t("admin.organizations.table.columns.status"),
			enableSorting: true,
			minSize: 100,
			cell: ({ row }) => {
				const statusCode = row.original.status.code;
				const statusName = row.original.status.name;
				const color = OrganizationStatusColorMap[statusCode] ?? "blue";

				return statusName ? (
					<BadgeWithDot size="md" type="modern" color={color}>
						{statusName}
					</BadgeWithDot>
				) : (
					<EmptyValue />
				);
			},
		},
		{
			id: "oAuthProvider.name",
			accessorFn: (row) => (row.oAuthProvider != null ? "thirdParty" : "local"),
			header: t("admin.organizations.table.columns.identityProvider"),
			enableSorting: true,
			minSize: 200,
			cell: ({ row }) => {
				return (
					<div className="whitespace-nowrap max-w-xs">
						<p className="text-sm font-medium text-primary truncate">
							{row.original.oAuthProvider != null
								? t(
										"org-admin.settings.security.identityProvider.thirdPartyAuthentication",
									)
								: t(
										"org-admin.settings.security.identityProvider.localAuthentication",
									)}
						</p>
					</div>
				);
			},
		},
		{
			id: "mfaEnabled",
			accessorKey: "mfaEnabled",
			header: t("admin.organizations.table.columns.mfa"),
			enableSorting: false,
			minSize: 100,
			cell: ({ row }) => (
				<BadgeWithDot
					size="md"
					type="modern"
					color={row.original.mfaEnabled ? "success" : "gray"}
				>
					{row.original.mfaEnabled
						? t("org-admin.settings.security.mfa.enabled")
						: t("org-admin.settings.security.mfa.disabled")}
				</BadgeWithDot>
			),
		},
		{
			id: "totalUserCount",
			accessorKey: "totalUserCount",
			header: t("admin.organizations.table.columns.userCount"),
			enableSorting: true,
			minSize: 175,
			cell: ({ row }) =>
				row.original.totalUserCount != null &&
				row.original.totalUserCount >= 0 ? (
					<span className="text-sm font-medium">
						{row.original.totalUserCount}
					</span>
				) : (
					<EmptyValue />
				),
		},
		{
			id: "createdat",
			accessorKey: "createdat",
			header: t("admin.organizations.table.columns.created"),
			enableSorting: true,
			minSize: 175,
			cell: ({ row }) => {
				return row.original.createdAt ? (
					<span className="text-sm font-medium">
						{formatLongDate(row.original.createdAt)}
					</span>
				) : (
					<EmptyValue />
				);
			},
		},
		{
			id: "updatedat",
			accessorKey: "updatedat",
			header: t("admin.organizations.table.columns.lastUpdate"),
			enableSorting: true,
			minSize: 175,
			cell: ({ row }) => {
				return row.original.updatedAt ? (
					<span className="text-sm font-medium">
						{formatLongDate(row.original.updatedAt)}
					</span>
				) : (
					<EmptyValue />
				);
			},
		},
		{
			id: "id",
			accessorKey: "id",
			header: t("admin.organizations.table.columns.id"),
			enableSorting: true,
			minSize: 200,
			cell: ({ row }) => {
				return row.original.id ? (
					<div className="inline-flex min-w-0 max-w-full">
						<CopyableId value={row.original.id} />
					</div>
				) : (
					<EmptyValue />
				);
			},
		},
	];

	const noOrganizations = (isSearchEmpty: boolean) => {
		if (isSearchEmpty) {
			return (
				<TableSearchEmptyState titleKey="admin.organizations.table.empty.noSearchResults" />
			);
		}

		return (
			<div className="flex flex-col items-center justify-center">
				<div className="relative flex items-center justify-center w-[480px] h-[480px]">
					<img
						alt={t("admin.organizations.empty.backgroundGridAlt")}
						className="absolute w-full h-full"
					/>
					<div className="relative flex flex-col items-center justify-center">
						<img
							alt={t("admin.organizations.empty.organizationAlt")}
							className=" w-[152px] h-[118px] object-contain"
						/>
						<div className="mt-4 text-center">
							<h2 className="font-semibold text-primary text-md">
								{t("admin.organizations.empty.title")}
							</h2>
							<p className="text-sm text-tertiary">
								{t("admin.organizations.empty.description")}
							</p>
						</div>
						<Button
							size="sm"
							color="primary"
							data-testid="platform-org-create-open-btn"
							onClick={() => {
								setIsOpen(true);
							}}
							className="mt-4"
						>
							{t("admin.organizations.table.actions.createOrganization")}
						</Button>
					</div>
				</div>
			</div>
		);
	};

	useEffect(() => {
		if (!pageCount) return;

		const prefetchRange = 1;
		for (let i = 1; i <= prefetchRange; i++) {
			const nextPageIndex = pageIndex + i;
			if (nextPageIndex < pageCount) {
				const nextStartIndex = nextPageIndex * pageSize + 1;
				queryClient.prefetchQuery(
					organizationQueryOptions.list(nextStartIndex, pageSize, {
						SearchTerm: searchTerm,
						...listQueryParams,
						...sortParams,
					}),
				);
			}
		}

		for (let i = 1; i <= prefetchRange; i++) {
			const prevPageIndex = pageIndex - i;
			if (prevPageIndex >= 0) {
				const prevStartIndex = prevPageIndex * pageSize + 1;
				queryClient.prefetchQuery(
					organizationQueryOptions.list(prevStartIndex, pageSize, {
						SearchTerm: searchTerm,
						...listQueryParams,
						...sortParams,
					}),
				);
			}
		}
	}, [
		pageIndex,
		pageSize,
		pageCount,
		searchTerm,
		listQueryParams,
		sortParams,
		queryClient,
	]);

	if (allOrgs.isError) {
		return (
			<div className="space-y-0">
				<div className="flex flex-col items-center justify-center py-12">
					<div className="text-center">
						<h3 className="text-sm font-medium text-error">
							{t("admin.organizations.errors.failedToLoadOrganizations")}
						</h3>
						<p className="text-sm text-tertiary mt-1">
							{allOrgs.error?.message}
						</p>
						<Button
							size="sm"
							color="primary"
							onClick={handleRefresh}
							className="mt-3"
						>
							{t("common.actions.tryAgain")}
						</Button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<>
			<PageLayout
				header={
					<PageHeader
						type="landing"
						title={t("admin.organizations.table.title")}
						description={t("admin.organizations.table.description")}
						actions={
							data?.length > 0
								? [
										{
											label: t(
												"admin.organizations.table.actions.createOrganization",
											),
											testId: "platform-org-create-open-btn",
											color: "accent",
											onPress: () => setIsOpen(true),
										},
									]
								: undefined
						}
						overflowControl={<div ref={setTableOptionsHost} />}
						overflowAriaLabel={t("common.tableActions.options")}
					/>
				}
			>
				<CustomTableWrapper
					tableKey={TABLE_KEY}
					// Data
					data={data}
					columns={organizationColumns}
					// Display
					title={t("admin.organizations.table.title")}
					showHeaderPadding={false}
					zebraStriping
					// Loading States
					isLoading={allOrgs.isLoading}
					isFetching={allOrgs.isFetching}
					isRefreshing={isRefreshing}
					onRefresh={handleRefresh}
					// Pagination
					paginationMode="server"
					externalPaginationState={{ pageSize, pageIndex }}
					onPaginationChange={handlePaginationChange}
					pageCount={pageCount}
					rowCount={orgCount.data}
					totalRowCount={unFilteredOrgCount.data}
					// Sorting
					sortFieldMapping={sortFieldMapping}
					defaultSortField="organizationName"
					onSortChange={({ fields, descending }) => {
						setSortParams({
							"OrderBy.Fields": fields,
							"OrderBy.Descending": descending,
						});
					}}
					// Filtering
					filterPlaceholder={t("admin.organizations.table.search.placeholder")}
					filterValue={searchTerm}
					onDebouncedFilterChange={handleDebouncedSearchChange}
					filtersTrailingContent={
						<OrganizationsTableFilters
							value={filters}
							onChange={handleFilterChange}
						/>
					}
					// Table Actions
					enableTableActions
					filterLayout="bar"
					filterBarType="landing"
					filterBarClassName="pt-0"
					tableOptionsPortalTarget={tableOptionsHost}
					hasActiveFilters={Object.keys(filterParams).length > 0}
					onExportClick={handleExport}
					isExportLoading={exportMutation.isPending}
					// Features
					enableColumnReordering
					onRowClick={handleRowClick}
					enableColumnResizing
					// Persistence - Initial values from saved settings
					initialSortingState={tableSettings.sorting}
					initialColumnOrder={normalizeColumnOrder(tableSettings.columnOrder)}
					initialColumnVisibility={tableSettings.columnVisibility}
					settingsLoaded={settingsLoaded}
					// Persistence - Save callbacks
					onSortingStateChange={saveSorting}
					onColumnOrderChange={(order) => {
						saveColumnOrder(normalizeColumnOrder(order));
					}}
					onColumnVisibilityChange={saveColumnVisibility}
				/>

				{!allOrgs.isLoading && !allOrgs.isFetching && data?.length <= 0
					? noOrganizations(
							(unFilteredOrgCount.data ?? 0) > 0 && (orgCount.data ?? 0) === 0,
						)
					: null}
			</PageLayout>

			<CreateOrgModal
				isOpen={isOpen}
				setIsOpen={setIsOpen}
				handleClose={() => {
					setIsOpen(false);
				}}
			/>
			<Outlet />
		</>
	);
}
